import React from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { useResizeDetector } from 'react-resize-detector';
import { Menu, MenuItem, Tabs, Tab } from '@mui/material';

import { AuthorizerContext } from 'app/providers/acl/AclProvider';
import { type BaseProps, type TabProps, type TabType } from './hooks/useTabs';
import useSettings from './hooks/useSettings';
import useUserData from './hooks/useUserData';
import useIsModuleDisabled from './hooks/useIsModuleDisabled';
import Icon from './Icon';

const menuTabWidth = 48;
const tabMinWidth = 150;
const maxMenuItems = 6;

interface TabsWithMoreProps<T> {
	/** The tabs that should be displayed */
	tabs: TabType<T>[];
	/** The tabs props */
	tabProps: TabProps;
	/** The current tab index */
	currentTabId?: string;
	/** Function called when the tab index changes */
	onTabChange: (tabId: string) => void;
}

export default function TabsWithMore<T = any>(props: TabsWithMoreProps<T>) {
	const dispatch = useDispatch();
	const { sysSettings, t } = useSettings();
	const userData = useUserData();
	const intl = useIntl();
	const isModuleDisabled = useIsModuleDisabled();
	const authorizer = React.useContext(AuthorizerContext);

	const { ref, width = 0 } = useResizeDetector({ handleHeight: false });

	const baseProps: BaseProps = { dispatch, sysSettings, isModuleDisabled, authorizer, userData, intl, t };

	const tabsWidth = width - menuTabWidth;
	const visibleTabs = Math.max(Math.floor(tabsWidth / tabMinWidth), 0);
	const tabWidth = props.tabs.length <= visibleTabs ? 'auto' : Math.floor(tabsWidth / visibleTabs);

	const currentTabIndex = props.tabs.findIndex(tab => tab.id === props.currentTabId);

	const [anchorEl, setAnchorEl] = React.useState(null);
	const open = Boolean(anchorEl);

	const handleClick = event => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const isTabDisabled = (tab: TabType<T>) => {
		return tab.disabled?.({ ...props.tabProps, ...baseProps }) || tab.content === null;
	};

	return (
		<>
			<Tabs
				ref={ref}
				indicatorColor="primary"
				textColor="primary"
				value={Math.min(Math.max(currentTabIndex, 0), visibleTabs)}
				sx={theme => ({ borderBottom: `1px solid ${theme.palette.divider}` })}
			>
				{props.tabs.map(
					(tab, index) =>
						index < visibleTabs && (
							<Tab
								key={index}
								label={t(tab.title)}
								disabled={isTabDisabled(tab)}
								onClick={() => props.onTabChange(tab.id)}
								sx={{
									width: tabWidth,
									minWidth: tabMinWidth,
									paddingY: 0
								}}
							/>
						)
				)}
				{props.tabs.length > visibleTabs && (
					<Tab
						icon={<Icon icon="menu-down" />}
						onClick={handleClick}
						sx={{
							width: menuTabWidth,
							minWidth: menuTabWidth,
							paddingY: 0
						}}
					/>
				)}
			</Tabs>
			<Menu
				anchorEl={anchorEl}
				keepMounted
				open={open}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				transformOrigin={{ vertical: 'top', horizontal: 'center' }}
				slotProps={{
					paper: {
						sx: theme => ({
							maxHeight: 48 * maxMenuItems,
							[theme.breakpoints.down('sm')]: { width: '100%' }
						})
					}
				}}
			>
				{props.tabs.map(
					(tab, index) =>
						index >= visibleTabs && (
							<MenuItem
								key={tab.id}
								dense
								selected={tab.id === props.currentTabId}
								disabled={isTabDisabled(tab)}
								onClick={() => {
									handleClose();
									props.onTabChange(tab.id);
								}}
							>
								{t(tab.title)}
							</MenuItem>
						)
				)}
			</Menu>
		</>
	);
}
